import type * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Controller } from 'react-hook-form'
import type { Control, UseFormRegister, FieldErrors } from 'react-hook-form'

import { TransportType } from '#/shared/api/generated/shipment/models'
import { MANAGE_FIELD_KEY, useIsFieldHidden } from '#/shared/lib/manage-fields'
import { DatePicker } from '#/shared/ui/date-picker'
import { FormCard } from '#/shared/ui/form-card'
import { Input } from '#/shared/ui/input'
import { MultiSelect } from '#/shared/ui/multi-select'
import type { MultiSelectOption } from '#/shared/ui/multi-select'
import { Select } from '#/shared/ui/select'
import type { ShipmentFormData } from '../model/shipment-schema'
import { useTagsList } from '../model/use-tags-list'
import { SourceCombobox } from './source-combobox'

interface ShipmentCardProps {
  control: Control<ShipmentFormData>
  register: UseFormRegister<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
  bare?: boolean
  hideNotes?: boolean
  hideTitle?: boolean
  compact?: boolean
  tagSeedOptions?: MultiSelectOption[]
  /**
   * Sidebar order layout: only the ship/pickup/delivery dates + trailer in a
   * 2×2 grid (no source/tags/notes).
   */
  dateGrid?: boolean
}

const FIELD_PROPS = {
  inputSize: 'sm' as const,
  labelSize: 'sm' as const,
  clearable: true,
}

const TRAILER_VALUES = [
  TransportType.OPEN,
  TransportType.ENCLOSED,
  TransportType.DRIVE_AWAY,
] as const

function useTrailerOptions() {
  const { t } = useTranslation()
  return TRAILER_VALUES.map((value) => ({
    value,
    label: t(`orderCreate.shipment.trailerOptions.${value}`),
  }))
}

type DateFieldName = 'shipDate' | 'pickupDate' | 'deliveryDate'

function SourceField({
  control,
  errors,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
}) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name="sourceId"
      render={({ field: idField }) => (
        <Controller
          control={control}
          name="source"
          render={({ field }) => (
            <SourceCombobox
              label={t('orderCreate.shipment.source')}
              value={field.value}
              onValueChange={(value) => {
                field.onChange(value)
                idField.onChange('')
              }}
              onProviderSelect={(option) => idField.onChange(option.value)}
              error={!!errors.source}
            />
          )}
        />
      )}
    />
  )
}

function TrailerSourceTagsRow({
  control,
  errors,
  seedOptions,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
  seedOptions?: MultiSelectOption[]
}) {
  const { t } = useTranslation()
  const trailerOptions = useTrailerOptions()
  const showTags = !useIsFieldHidden(MANAGE_FIELD_KEY.shipmentTags)
  return (
    <div className="flex items-start gap-2.5">
      <div className="flex-1">
        <Controller
          control={control}
          name="trailer"
          render={({ field }) => (
            <Select
              selectSize="sm"
              labelSize="sm"
              label={t('orderCreate.shipment.trailer')}
              placeholder={t('orderCreate.shipment.placeholders.trailer')}
              options={trailerOptions}
              value={field.value}
              onValueChange={field.onChange}
              error={!!errors.trailer}
            />
          )}
        />
      </div>
      <div className="flex-1">
        <SourceField control={control} errors={errors} />
      </div>
      {showTags && (
        <div className="flex-1">
          <TagsField
            control={control}
            errors={errors}
            seedOptions={seedOptions}
          />
        </div>
      )}
    </div>
  )
}

function TagsField({
  control,
  errors,
  seedOptions,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
  seedOptions?: MultiSelectOption[]
}) {
  const { t } = useTranslation()
  const tags = useTagsList({ seedOptions })
  return (
    <Controller
      control={control}
      name="tags"
      render={({ field }) => (
        <MultiSelect
          labelSize="sm"
          label={t('orderCreate.shipment.tags')}
          options={tags.options}
          value={field.value}
          onValueChange={field.onChange}
          onOpenChange={tags.onOpenChange}
          searchValue={tags.searchValue}
          onSearchChange={tags.onSearchChange}
          loading={tags.isLoading}
          onLoadMore={tags.onLoadMore}
          hasMore={tags.hasMore}
          isFetchingMore={tags.isFetchingMore}
          error={!!errors.tags}
          allowCreate
          onCreateOption={(name) => {
            const value = tags.createDraftTag(name)
            if (!field.value.includes(value)) {
              field.onChange([...field.value, value])
            }
          }}
        />
      )}
    />
  )
}

function DateField({
  name,
  labelKey,
  control,
  errors,
  align,
  placeholder,
}: {
  name: DateFieldName
  labelKey: string
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
  align?: 'left' | 'center' | 'right'
  placeholder?: string
}) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <DatePicker
          instantApply
          labelSize="sm"
          align={align}
          label={t(labelKey)}
          placeholder={placeholder}
          dateFormat="MMM D"
          value={field.value}
          onValueChange={field.onChange}
          error={!!errors[name]}
        />
      )}
    />
  )
}

function DatesRow({
  control,
  errors,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="flex-1">
        <DateField
          name="shipDate"
          labelKey="orderCreate.shipment.shipDate"
          control={control}
          errors={errors}
          align="left"
        />
      </div>
      <div className="flex-1">
        <DateField
          name="pickupDate"
          labelKey="orderCreate.shipment.pickupDate"
          control={control}
          errors={errors}
          placeholder=""
        />
      </div>
      <div className="flex-1">
        <DateField
          name="deliveryDate"
          labelKey="orderCreate.shipment.deliveryDate"
          control={control}
          errors={errors}
          placeholder=""
        />
      </div>
    </div>
  )
}

function TrailerSelect({
  control,
  errors,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
}) {
  const { t } = useTranslation()
  const trailerOptions = useTrailerOptions()
  return (
    <Controller
      control={control}
      name="trailer"
      render={({ field }) => (
        <Select
          selectSize="sm"
          labelSize="sm"
          label={t('orderCreate.shipment.trailer')}
          placeholder={t('orderCreate.shipment.placeholders.trailer')}
          options={trailerOptions}
          value={field.value}
          onValueChange={field.onChange}
          error={!!errors.trailer}
        />
      )}
    />
  )
}

function CompactShipmentFields({
  control,
  errors,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-25 shrink-0">
        <DateField
          name="shipDate"
          labelKey="orderCreate.shipment.shipDate"
          control={control}
          errors={errors}
          align="left"
        />
      </div>
      <div className="flex-1">
        <TrailerSelect control={control} errors={errors} />
      </div>
      <div className="flex-1">
        <SourceField control={control} errors={errors} />
      </div>
    </div>
  )
}

function DateGridFields({
  control,
  errors,
  compact = false,
}: {
  control: Control<ShipmentFormData>
  errors: FieldErrors<ShipmentFormData>
  compact?: boolean
}) {
  return (
    <>
      <div className="flex items-start gap-2.5">
        <div className="flex-1">
          <DateField
            name="shipDate"
            labelKey="orderCreate.shipment.shipDate"
            control={control}
            errors={errors}
            align="left"
          />
        </div>
        <div className="flex-1">
          <TrailerSelect control={control} errors={errors} />
        </div>
      </div>
      {/* Quotes only carry the ship date + trailer; orders add the estimated
          pickup/delivery dates. */}
      {!compact && (
        <div className="flex items-start gap-2.5">
          <div className="flex-1">
            <DateField
              name="pickupDate"
              labelKey="orderCreate.shipment.pickupDate"
              control={control}
              errors={errors}
              placeholder=""
            />
          </div>
          <div className="flex-1">
            <DateField
              name="deliveryDate"
              labelKey="orderCreate.shipment.deliveryDate"
              control={control}
              errors={errors}
              placeholder=""
            />
          </div>
        </div>
      )}
    </>
  )
}

function ShipmentFields({
  control,
  register,
  errors,
  hideNotes,
  tagSeedOptions,
}: Omit<ShipmentCardProps, 'bare' | 'compact'>) {
  const { t } = useTranslation()
  const showCustomerNote = !useIsFieldHidden(
    MANAGE_FIELD_KEY.shipmentCustomerNote,
  )
  return (
    <>
      <TrailerSourceTagsRow
        control={control}
        errors={errors}
        seedOptions={tagSeedOptions}
      />
      <DatesRow control={control} errors={errors} />
      {!hideNotes && (
        <>
          <Input
            {...FIELD_PROPS}
            {...register('loadBoardNote')}
            label={t('orderCreate.shipment.loadBoardNote')}
            error={!!errors.loadBoardNote}
          />
          {showCustomerNote && (
            <Input
              {...FIELD_PROPS}
              {...register('customerNote')}
              label={t('orderCreate.shipment.customerNote')}
              error={!!errors.customerNote}
            />
          )}
        </>
      )}
    </>
  )
}

function BareWrapper({
  children,
  hideTitle,
}: {
  children: React.ReactNode
  hideTitle?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full flex-col gap-2.5 pb-2.5">
      {!hideTitle && (
        <div className="flex h-8 items-center px-2.5">
          <span className="text-xs uppercase text-gray-500">
            {t('orderCreate.shipment.title')}
          </span>
        </div>
      )}
      <div className="flex flex-col gap-2 px-2.5">{children}</div>
    </div>
  )
}

export function ShipmentCard({
  control,
  register,
  errors,
  bare = false,
  hideNotes,
  hideTitle,
  compact = false,
  tagSeedOptions,
  dateGrid = false,
}: ShipmentCardProps) {
  const { t } = useTranslation()
  if (dateGrid) {
    return (
      <BareWrapper hideTitle>
        <DateGridFields control={control} errors={errors} compact={compact} />
      </BareWrapper>
    )
  }
  if (compact) {
    return (
      <BareWrapper hideTitle>
        <CompactShipmentFields control={control} errors={errors} />
      </BareWrapper>
    )
  }
  if (bare) {
    return (
      <BareWrapper hideTitle={hideTitle}>
        <ShipmentFields
          control={control}
          register={register}
          errors={errors}
          hideNotes={hideNotes}
          tagSeedOptions={tagSeedOptions}
        />
      </BareWrapper>
    )
  }
  return (
    <FormCard title={t('orderCreate.shipment.title')} contentClassName="gap-2">
      <ShipmentFields
        control={control}
        register={register}
        errors={errors}
        hideNotes={hideNotes}
        tagSeedOptions={tagSeedOptions}
      />
    </FormCard>
  )
}
